import {
  useCallback, useState,
} from 'react';
import { translateErrorCode } from '~/_shared/types/responseErrors/responseErrorCodes.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { UploadStatus } from '../../_shared/components/uploadStatus/uploadStatus.component';
import { type ImportMethod } from '../../_shared/types/importMethod/importMethod';
import { uploadMapSpreadsheet } from '../../map/map.repository';
import { useImportData } from './useImportData';

type ImportFileDataProps = {
  clientId: number;
  file: File;
  importMethod: ImportMethod;
  mapId: number;
  spreadsheetId: number;
  userId: number;

  onProgressChange: (progress: number) => void;
  onStatusChange: (status: UploadStatus) => void;
  onSuccess: () => void;
};

export const useImportFileData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [t] = useTranslation();
  const { importData } = useImportData();

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  const importFileData = useCallback(async ({
    clientId, userId, mapId, spreadsheetId, importMethod, file, onProgressChange, onStatusChange, onSuccess,
  }: ImportFileDataProps) => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const uploadedSpreadsheet = await uploadMapSpreadsheet(clientId, file, {
        onProgress: ((progress: number) => {
          onProgressChange(progress / 3);
        }),
      });

      await importData({
        onImportSuccess: () => {
          onProgressChange(67);
        },
        clientId,
        importMethod,
        mapId,
        source: uploadedSpreadsheet.data.file_name,
        spreadsheetId,
        type: 'file',
        userId,
      })
        .then(() => {
          onSuccess();
          setIsLoading(false);
          onProgressChange(100);
          onStatusChange(UploadStatus.Success);
        });
    }
    catch (e) {
      setIsLoading(false);
      onStatusChange(UploadStatus.Error);

      if (e?.message) {
        setErrorMessage(translateErrorCode(e?.message, t));
      }
    }
  }, [importData, t]);

  return {
    isLoading,
    errorMessage,
    importFileData,
    clearErrorMessage,
  };
};
